import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ScrollNav from './ScrollNav';

function Footer() {
  const currentYear = new Date().getFullYear();
  const { t, i18n } = useTranslation();

  const isFr = i18n.language?.startsWith('fr');
  const isEn = i18n.language?.startsWith('en');

  const setLanguage = (lang: 'fr' | 'en') => {
    i18n.changeLanguage(lang);
  };

  const langBaseClass = 'text-xs transition-colors cursor-pointer';
  const langActiveClass = 'font-semibold text-primary-400';
  const langInactiveClass = 'text-foreground-500 hover:text-foreground-500';

  return (
    <footer className="relative bg-background-100 overflow-hidden">
      <ScrollNav />
      {/* Water wave divider */}
      <div className="relative h-12 md:h-16 overflow-hidden" aria-hidden="true">
        <div className="footer-wave bg-primary-500/60" />
        <div
          className="footer-wave bg-accent-500/50"
          style={{ animationDelay: '-4s', animationDuration: '12s' }}
        />
      </div>

      <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/4348d35aef10b02e64c1a80f787cca17/52f74c8d2b3c4f047b22c4e6b94b8b4b.jpeg"
                alt="WorldApeiron"
                className="h-10 w-10 object-contain"
              />
              <span className="font-heading font-bold text-lg text-foreground-50">
                World<span className="text-primary-400">Apeiron</span>
              </span>
            </div>
            <p className="text-sm text-foreground-400 leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/worldapeiron?igsi=c3dhcmQ3bmU2eXpz" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400 hover:text-primary-400 hover:bg-background-300/70 transition-colors">
                <i className="ri-instagram-line text-lg" />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61592903427936" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400 hover:text-primary-400 hover:bg-background-300/70 transition-colors">
                <i className="ri-facebook-line text-lg" />
              </a>
              <a href="https://x.com/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400 hover:text-primary-400 hover:bg-background-300/70 transition-colors">
                <i className="ri-twitter-x-line text-lg" />
              </a>
              <a href="https://www.tiktok.com/@web.entrepreneur49?_r=1&_t=ZN-99IQgHmeufe" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400 hover:text-primary-400 hover:bg-background-300/70 transition-colors">
                <i className="ri-tiktok-line text-lg" />
              </a>
              <a href="https://www.linkedin.com/in/matthew-dotse-5740a6427?utm_source=share_via&utm_content=profile&utm_medium=member_android" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400 hover:text-primary-400 hover:bg-background-300/70 transition-colors">
                <i className="ri-linkedin-line text-lg" />
              </a>
            </div>
          </div>

          {/* Ecosystem */}
          <div>
            <h4 className="font-heading font-semibold text-sm text-foreground-50 mb-4">{t('footer.ecosystem')}</h4>
            <ul className="space-y-3">
              <li><Link to="/shop" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">Shop</Link></li>
              <li><Link to="/work" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">Work</Link></li>
              <li><Link to="/jobs" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">Jobs</Link></li>
              <li><Link to="/learn" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">Learn</Link></li>
              <li><Link to="/apartments" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">Apartments</Link></li>
              <li><Link to="/pay" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">Pay</Link></li>
              <li><Link to="/forum" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">Forum</Link></li>
              <li><Link to="/cloud" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">Cloud</Link></li>
              <li><Link to="/ai" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">AI</Link></li>
              <li><Link to="/blog" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">Blog</Link></li>
              <li><Link to="/faq" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">FAQ</Link></li>
              <li><Link to="/support" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">Support</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading font-semibold text-sm text-foreground-50 mb-4">{t('footer.information')}</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">{t('footer.about')}</Link></li>
              <li><Link to="/" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">{t('footer.terms')}</Link></li>
              <li><Link to="/" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">{t('footer.privacy')}</Link></li>
              <li><Link to="/" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">{t('footer.legal')}</Link></li>
              <li><Link to="/" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors">{t('footer.careers')}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-sm text-foreground-50 mb-4">{t('footer.contact')}</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-mail-line text-sm text-primary-400" />
                </div>
                <span className="text-sm text-foreground-400">{t('footer.email')}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-phone-line text-sm text-primary-400" />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={`https://wa.me/${t('footer.phone').replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="WhatsApp"
                    className="inline-flex items-center gap-1.5 text-sm text-foreground-400 hover:text-primary-400 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-base" />
                    WhatsApp
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-sm text-primary-400" />
                </div>
                <span className="text-sm text-foreground-400">{t('footer.location')}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; {currentYear} WorldApeiron. {t('footer.rights')}
          </p>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setLanguage('fr')}
              className={`${langBaseClass} ${isFr ? langActiveClass : langInactiveClass}`}
            >
              Français
            </button>
            <span className="text-foreground-500 text-xs">·</span>
            <button
              type="button"
              onClick={() => setLanguage('en')}
              className={`${langBaseClass} ${isEn ? langActiveClass : langInactiveClass}`}
            >
              English
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
